import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

/**
 * "We expect | You typed" — the two figures a cashier is comparing, side by
 * side at the same size, so the comparison is visual rather than a sentence
 * to parse (F-FE-N139).
 *
 * `expected === null` is the blind count (cash): only the typed cell renders,
 * full width, so the operator cannot copy the number they are shown.
 *
 * There is deliberately NO `placeholder="0"` on the input. A grey placeholder
 * zero reads as an entered value: the field looked like it said 0, operators
 * moved on believing they had counted, and the day closed with the tender
 * stored as "never counted" (F-CTRL-N33).
 */
export function TenderPair({
  id,
  expected,
  value,
  onChange,
  formatCurrency,
}: {
  id: string
  expected: number | null
  value: string
  onChange: (next: string) => void
  formatCurrency: (n: number) => string
}) {
  const blind = expected === null
  return (
    <div
      className={cn(
        'grid overflow-hidden rounded-xl border',
        blind ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2',
      )}
    >
      {!blind && (
        <div className="border-b px-4 py-3 sm:border-b-0 sm:border-r">
          <div className="text-sm text-muted-foreground">We expect</div>
          <div className="mt-1 text-3xl font-semibold tabular-nums leading-tight tracking-tight">
            {formatCurrency(expected)}
          </div>
        </div>
      )}
      <div className="bg-muted/40 px-4 py-3">
        <label htmlFor={id} className="text-sm text-muted-foreground">
          You typed
        </label>
        {/* The currency sits beside the field, not inside it, so the operator
            can see what unit they are typing. On the blind cash count this is
            the only place the currency appears at all — that step has no
            "We expect" cell to carry it. It is a unit marker, never a value:
            an empty field still reads as empty (F-CTRL-N33). */}
        {/* Left-aligned on every step, blind cash included: the amount sits
            directly under the label that names it, and the four counting
            screens read the same as the operator steps through them. */}
        <div className="mt-1 flex items-baseline gap-1.5">
          <span
            aria-hidden="true"
            className="text-3xl font-semibold leading-none tracking-tight text-muted-foreground"
          >
            {currencyPrefix(formatCurrency)}
          </span>
          <Input
            id={id}
            type="text"
            inputMode="decimal"
            autoFocus
            value={value}
            onChange={(e) => onChange(e.target.value.replace(/[^0-9.]/g, ''))}
            className={cn(
              'h-11 border-0 bg-transparent px-0 text-3xl font-semibold tabular-nums tracking-tight shadow-none',
              'focus-visible:ring-0 focus-visible:ring-offset-0',
              'w-full text-left',
            )}
          />
        </div>
      </div>
    </div>
  )
}

/**
 * The currency marker exactly as the expected figure renders it — derived from
 * the tenant's own formatter rather than hardcoded, so "Rs" and "$" stay in step
 * with `We expect` in the cell next door.
 */
function currencyPrefix(formatCurrency: (n: number) => string): string {
  return formatCurrency(0).replace(/[\d.,\s]+$/, '').trim()
}
